import { useState } from 'react'
import { Send, X, Check } from 'lucide-react'
import { useT } from '@/i18n'
import {
  replySupportTicket,
  closeSupportTicket,
  resolveSupportAppeal,
  type AdminSupportTicket,
} from '@/lib/adapters'
import { RelativeTime } from '@/components/DataDisplay'
import { Tag } from '@/components/StatusBadge'
import { Dialog } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Field, Textarea } from '@/components/ui/input'
import { useToast } from '@/providers/UiProviders'
import { useAuth } from '@/providers/AuthProvider'
import { PERMISSIONS } from '@/lib/permissions'

// SpamBotDispatcher prefixes freeze/ban-appeal tickets with one of these tags;
// AbuseNotificationsBotDispatcher prefixes channel-ban appeals with '[ChannelAppeal:{channelId}]'.
// All bots share one open ticket per user, so a tag can land on any message in the thread, not
// just the first. Shared between SupportScreen (excludes these) and AppealsScreen (only these) so
// the two lists never overlap and admins don't have to guess which section a ticket belongs to.
export const APPEAL_TAGS = ['[Заморозка]', '[Бан]', '[ChannelAppeal:']
export const isAppealTicket = (ticket: AdminSupportTicket) =>
  ticket.messages.some((m) => APPEAL_TAGS.some((tag) => m.text.startsWith(tag)))

export function TicketThread({ ticket, onChanged }: { ticket: AdminSupportTicket; onChanged: () => void }) {
  const t = useT()
  const { toast } = useToast()
  const { can } = useAuth()
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [appealAction, setAppealAction] = useState<'approve' | 'reject' | null>(null)

  const canModerate = can(PERMISSIONS.USERS_MODERATE)
  const isAppeal = isAppealTicket(ticket)

  const send = async () => {
    if (!text.trim()) return
    setBusy(true)
    try {
      await replySupportTicket(ticket.id, text.trim())
      setText('')
      toast(t.users.actionDone, { tone: 'success' })
      onChanged()
    } catch (e) {
      toast(t.users.actionFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
    } finally {
      setBusy(false)
    }
  }

  const close = async () => {
    setBusy(true)
    try {
      await closeSupportTicket(ticket.id)
      toast(t.users.actionDone, { tone: 'success' })
      onChanged()
    } catch (e) {
      toast(t.users.actionFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="max-w-xl space-y-3">
      {isAppeal && <Tag>{t.support.appealBadge}</Tag>}
      <div className="space-y-2">
        {ticket.messages.map((m, i) => {
          // "admin:{username}" - a human admin replied. "bot:{botName}" - the bot's own automated
          // reply, logged (not re-sent) purely so the admin sees the full conversation, not just
          // the user's side of it. Both render the same way, right-aligned with a name tag.
          const isAdmin = m.from.startsWith('admin:') || m.from.startsWith('bot:')
          const label = m.from.replace('admin:', '').replace('bot:', '')
          return (
            <div key={i} className={isAdmin ? 'text-right' : ''}>
              <div
                className={
                  'inline-block max-w-[80%] rounded-md px-2.5 py-1.5 text-sm ' +
                  (isAdmin ? 'bg-accent-subtle text-primary' : 'bg-surface-3 text-primary')
                }
              >
                {m.text}
              </div>
              <div className="mt-0.5 text-xs text-tertiary">
                {isAdmin && <Tag>{label}</Tag>} <RelativeTime value={m.at} />
              </div>
            </div>
          )
        })}
      </div>

      {ticket.status === 'closed' ? (
        <div className="text-sm text-tertiary">
          {t.support.closedBy}: {ticket.closed_by_admin ?? '—'}
          {ticket.closed_at ? (
            <>
              {' · '}
              <RelativeTime value={ticket.closed_at} />
            </>
          ) : null}
        </div>
      ) : (
        canModerate && (
          <div className="space-y-3">
            {isAppeal && (
              <div className="flex items-center gap-2">
                <Button variant="primary" onClick={() => setAppealAction('approve')} disabled={busy}>
                  <Check size={13} />
                  {t.support.approveAppeal}
                </Button>
                <Button variant="danger" onClick={() => setAppealAction('reject')} disabled={busy}>
                  <X size={13} />
                  {t.support.rejectAppeal}
                </Button>
              </div>
            )}
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t.support.replyPlaceholder}
              rows={2}
            />
            <div className="flex items-center gap-2">
              <Button variant="primary" onClick={() => void send()} disabled={busy || !text.trim()}>
                <Send size={13} />
                {t.support.reply}
              </Button>
              <Button variant="ghost" onClick={() => void close()} disabled={busy}>
                <X size={13} />
                {t.support.close}
              </Button>
            </div>
          </div>
        )
      )}

      {appealAction && (
        <ResolveAppealDialog
          ticketId={ticket.id}
          kind={appealAction}
          onClose={() => setAppealAction(null)}
          onDone={() => {
            setAppealAction(null)
            onChanged()
          }}
        />
      )}
    </div>
  )
}

function ResolveAppealDialog({
  ticketId,
  kind,
  onClose,
  onDone,
}: {
  ticketId: string
  kind: 'approve' | 'reject'
  onClose: () => void
  onDone: () => void
}) {
  const t = useT()
  const { toast } = useToast()
  const [resolution, setResolution] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const submit = async () => {
    setSubmitting(true)
    try {
      await resolveSupportAppeal(ticketId, kind === 'approve', resolution.trim() || undefined)
      toast(t.users.actionDone, { tone: 'success' })
      onDone()
    } catch (e) {
      toast(t.users.actionFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={kind === 'approve' ? t.support.approveAppeal : t.support.rejectAppeal}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button variant={kind === 'reject' ? 'danger' : 'primary'} onClick={() => void submit()} disabled={submitting}>
            {kind === 'approve' ? t.support.approveAppeal : t.support.rejectAppeal}
          </Button>
        </>
      }
    >
      <Field label={t.support.resolutionPlaceholder}>
        <Textarea value={resolution} onChange={(e) => setResolution(e.target.value)} rows={3} />
      </Field>
    </Dialog>
  )
}
